/**
 * Prepaid credit control (backend docs/BILLING.md §4) as the portal edits it: balance
 * alert tiers, enforcement mode, grace and auto-suspend. Kept out of the drawer so the
 * rules can be tested. The server is the authority — it answers 400 on bad tiers or a
 * negative grace, and the drawer shows its message.
 */
import {
  AlertSettingsRequest,
  AlertStatus,
  EnforceMode,
  EnforcementDTO,
  EnforcementRequest,
  SuspensionState,
  SwitchGateDTO,
  formatMoney,
} from '../../types/billing';
import { canEditBillingProfile } from './billingProfileRules';

/**
 * Who may CHANGE alerts and enforcement: the backend's credit reach — the organisation
 * above the account, or the platform. The same roles and the same "never your own
 * account" rule as the billing profile, so the one check is reused.
 */
export const canEditCreditControls = canEditBillingProfile;

/** What the drawer offers; DEFAULT clears the account's own mode (useGlobalDefault). */
export type EnforceChoice = EnforceMode | 'DEFAULT';

/**
 * The modes in plain words. The stored values stay OFF | SHADOW | ON; only the words
 * changed. SHADOW is "Soft": nothing is refused, and what the balance goes below zero
 * by (the shortfall) is invoiced.
 */
export const enforceModeLabels: Record<EnforceMode, string> = {
  OFF: 'Off — no balance check',
  SHADOW: 'Soft — calls and messages continue below zero; the shortfall is invoiced',
  ON: 'Enforce — new calls and sends are refused once the balance reaches −grace',
};

/** One word per mode, for facts and the "platform default (…)" option. */
export const enforceModeShort: Record<EnforceMode, string> = {
  OFF: 'Off',
  SHADOW: 'Soft',
  ON: 'Enforce',
};

export const modeShort = (m?: EnforceMode | string | null): string =>
  m ? enforceModeShort[m as EnforceMode] || m : '-';

/** The mode a choice ends up as: DEFAULT follows the platform's. */
export const resultingMode = (choice: EnforceChoice, globalEnforce?: EnforceMode | string | null): string | null =>
  choice === 'DEFAULT' ? globalEnforce || null : choice;

/**
 * Saving asks first when it would turn Enforce on: from then on the switches refuse
 * live calls at the floor. Re-saving an account already on Enforce does not ask.
 */
export const needsEnforceConfirm = (
  state: Pick<EnforcementDTO, 'effectiveMode' | 'globalEnforce'>,
  choice: EnforceChoice,
): boolean => resultingMode(choice, state.globalEnforce) === 'ON' && state.effectiveMode !== 'ON';

/** What the confirm step says, with the floor in money. */
export const enforceConfirmText = (name: string, grace: number, currency: string): string =>
  `Switch ${name} to Enforce? Once its balance is at or below ${formatMoney(-Math.abs(grace), currency)} (−grace), `
  + 'the tata switches refuse its live inbound and outbound calls (Xtreme\'s traffic included), and cpass refuses new '
  + 'calls and WhatsApp sends. A top-up restores them within about a minute.';

/** Grace as a floor: "floor = −grace". Blank or invalid grace reads as 0. */
export const graceFloorText = (graceText: string, currency: string): string => {
  const n = Number((graceText ?? '').trim() || 0);
  const g = Number.isFinite(n) && n >= 0 ? n : 0;
  return `Floor = −grace: with a grace of ${formatMoney(g, currency)}, Enforce refuses once the balance reaches `
    + `${formatMoney(-g, currency)}. Soft lets it run below that and the shortfall is invoiced.`;
};

export type Tone = 'green' | 'orange' | 'red' | 'gray';

export const toneClass: Record<Tone, string> = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  gray: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

/** "Switch calls (tata switches)": what the switches' balance gate does to this account now. */
export const switchGateView = (g?: SwitchGateDTO | null): { text: string; tone: Tone; detail?: string } => {
  if (!g) return { text: 'Not reported', tone: 'gray' };
  const detail = g.listed ? `Gate entry covers ${g.patterns} business id(s) and ${g.dids} DID(s).` : undefined;
  if (!g.listed) return { text: 'Allowed', tone: 'green' };
  if (g.mode === 'ENFORCE') return { text: 'BLOCKED on the switches — top up to restore (takes ≤1 min)', tone: 'red', detail };
  if (g.mode === 'SHADOW') return { text: 'Would block (Soft: not blocked)', tone: 'orange', detail };
  return { text: 'Listed on the switches (mode unknown)', tone: 'orange', detail };
};

/** The shortfall panel: prepaid accounts whose balance is below zero. */
export const showShortfall = (billingType?: string | null, balance?: number | null): boolean =>
  (billingType || '').toUpperCase() === 'PREPAID' && typeof balance === 'number' && balance < 0;

/** Where an invoice opens in the existing Invoices page. */
export const invoiceLink = (invoiceId: number): string => `/billing/invoices?invoice=${invoiceId}`;

export const suspensionStateLabels: Record<SuspensionState, string> = {
  NONE: 'Not suspended',
  SHADOW_SUSPENDED: 'Would be suspended (Soft, nothing blocked)',
  SUSPENDED: 'Suspended',
  OPERATOR_LIFTED: 'Suspension lifted by an operator',
};

export const suspensionStateClass = (s?: SuspensionState | null): string =>
  s === 'SUSPENDED'
    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    : s === 'SHADOW_SUSPENDED'
      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
      : s === 'OPERATOR_LIFTED'
        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';

export const alertStatusLabels: Record<AlertStatus, string> = {
  SENT: 'Sent',
  FAILED: 'Failed',
  LOGGED: 'Logged only',
  NO_RECIPIENT: 'No recipient',
  SUPERSEDED: 'Superseded',
};

export const alertStatusClass = (s?: AlertStatus | string | null): string =>
  s === 'SENT'
    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    : s === 'FAILED' || s === 'NO_RECIPIENT'
      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

/**
 * What suspension does and does not stop. Said next to the auto-suspend switch, because
 * "suspended" otherwise reads as "this customer can no longer cost us money".
 */
export const AUTO_SUSPEND_SCOPE =
  'Suspension only blocks usage cpass itself originates: WhatsApp sends and cpass credit holds. '
  + 'It does NOT stop calls through the switches, running voicex campaigns, the cpass voice dialler or AI reply calls. '
  + 'Switch calls are refused only by Enforce mode, through the switches\' balance gate shown above.';

/** `50, 20, 0` (or spaces) → [50, 20, 0]; sorted high to low, duplicates refused. */
export const parseTierPercents = (text: string): { tiers: number[] } | { error: string } => {
  const parts = (text || '').split(/[\s,;]+/).map((p) => p.replace(/%$/, '')).filter(Boolean);
  const tiers: number[] = [];
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p)) return { error: `"${p}" is not a whole percentage` };
    const n = Number(p);
    if (n > 100) return { error: 'A tier is a percentage of the last top-up: 0 to 100' };
    if (tiers.includes(n)) return { error: `${n}% is listed twice` };
    tiers.push(n);
  }
  return { tiers: tiers.sort((a, b) => b - a) };
};

export const formatTierPercents = (tiers?: number[] | null): string => (tiers || []).join(', ');

/** A money field: '' = unchanged (undefined), else a number ≥ 0 or an error. */
const nonNegative = (text: string, what: string): { value?: number } | { error: string } => {
  const t = (text ?? '').trim();
  if (t === '') return {};
  const n = Number(t);
  if (!Number.isFinite(n)) return { error: `${what} must be a number` };
  if (n < 0) return { error: `${what} cannot be negative` };
  return { value: n };
};

/**
 * The PUT alert-settings body. Following the default sends `useDefaultTiers` and no
 * list (the server clears the account's own); otherwise the parsed list, where an empty
 * list means "no percentage tiers", not "default".
 */
export const alertSettingsBody = (form: {
  useDefault: boolean;
  tiersText: string;
  lowBalanceThreshold: string;
}): { body: AlertSettingsRequest } | { error: string } => {
  const body: AlertSettingsRequest = {};
  if (form.useDefault) {
    body.useDefaultTiers = true;
  } else {
    const parsed = parseTierPercents(form.tiersText);
    if ('error' in parsed) return parsed;
    body.alertTierPercents = parsed.tiers;
  }
  const threshold = nonNegative(form.lowBalanceThreshold, 'Low-balance threshold');
  if ('error' in threshold) return threshold;
  if (threshold.value !== undefined) body.lowBalanceThreshold = threshold.value;
  return { body };
};

/** The PUT enforcement body. DEFAULT → useGlobalDefault, never a mode. */
export const enforcementBody = (form: {
  mode: EnforceChoice;
  graceAmount: string;
  autoSuspend: boolean;
}): { body: EnforcementRequest } | { error: string } => {
  const body: EnforcementRequest = { autoSuspend: form.autoSuspend };
  if (form.mode === 'DEFAULT') body.useGlobalDefault = true;
  else body.enforceMode = form.mode;
  const grace = nonNegative(form.graceAmount, 'Grace');
  if ('error' in grace) return grace;
  if (grace.value !== undefined) body.graceAmount = grace.value;
  return { body };
};
